<template>
  <div v-if="orders && deliveryTypes">
    <slot />
    <div class="order-list-pills__table">
      <table>
        <thead :class="{ 'has-title': deliveryInfo }">
          <div class="delivery-info" v-if="deliveryInfo">
            {{ $t("page.order.submit.table.deliveryinformation") }}
          </div>
          <tr>
            <th v-for="(col, index) in columns" :key="col.label">
              <div v-if="col.key === 'assemblyArticleNumberSmallUvResistant'">
                {{ $t("page.order.submit.table.header.assemblyarticlenumber") }}
                <br />
                {{ $t("page.order.submit.table.header.rotorarticlenumber") }}
                <div v-if="col.key === 'amountOfCanisters'" class="tooltip">
                  {{ $t("page.order.submit.table.tooltip.refers") }}
                </div>
              </div>
              <div v-else>
                <div
                  v-if="![0, 1, 2].includes(index)"
                  class="header-column__icon"
                  @click="sortByKey($event, { key: col.key, type: col.type })"
                ></div>
                {{ $t(col.labelTranslationKey) }}
                <div
                  v-if="[0, 1, 2].includes(index)"
                  class="header-column__icon"
                  @click="sortByKey($event, { key: col.key, type: col.type })"
                ></div>
                <div v-if="col.key === 'amountOfCanisters'" class="tooltip">
                  {{ $t("page.order.submit.table.tooltip.refers") }}
                </div>
              </div>
            </th>

            <th v-for="type in deliveryTypes" :key="'column-' + type.id">
              <div
                v-if="type.maxCanisterCount"
                class="header-column__delivery"
                :class="{ 'header-column__delivery--disabled': isOnHoldType }"
              >
                <span>{{ isOnHoldType ? 0 : amountByDeliveryTypeId(type.id) }}/{{ type.maxCanisterCount }}</span>
                {{ $t(type.deliveryTypeTranslationKey).replace(/ .*/, "") }}
              </div>
              <div v-else>{{ $t(type.deliveryTypeTranslationKey) }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isFilter && sortedOrders.length > 0">
            <td colspan="10" class="table_filter">{{ $t("page.order.submit.table.filtered") }}</td>
          </tr>
          <tr v-if="sortedOrders.length === 0">
            <td colspan="10" class="table_filter">{{ $t("page.order.submit.table.empty") }}</td>
          </tr>
          <tr v-for="order in sortedOrders" :key="order.id" class="item">
            <td>{{ order.medKey }}</td>
            <td>{{ order.name }}</td>
            <td>{{ order.medId }}</td>
            <td>
              {{ getAssemblyArticleNumber(order) || $t("page.order.submit.table.value.unknownyet") }}
              <br />
              {{ order.rotorArticleNumber || $t("page.order.submit.table.value.unknownyet") }}
            </td>
            <td class="relative">
              <v-select
                title-property="name"
                value-property="key"
                :options="possibleCanisterSizesOptions(order.possibleCanisterSizes)"
                :disabled="order.possibleCanisterSizes.length === 1"
                v-model="order.canisterSize"
                placeholder="S"
              />
              <div class="tooltip-predefined">Predefined by database</div>
            </td>
            <td class="relative">
              <v-checkbox typeInput="checkbox" v-model="order.uvResistant" />
              <div class="tooltip-predefined">Predefined by database</div>
            </td>
            <td>
              <div
                class="counter-box"
                :class="{
                  'counter-box--active-tooltip': !isOnHoldType ? checkDeliveryTypeAmount(order) : false,
                }"
              >
                <span
                  class="counter-box__minus"
                  :class="{
                    'counter-box__minus--disabled': order.amountOfCanisters === 0,
                  }"
                  @click="decrease(order)"
                >
                  -
                </span>
                <span class="counter-box__number">
                  {{ order.amountOfCanisters }}
                </span>
                <span
                  class="counter-box__plus"
                  :class="{
                    'counter-box__plus--disabled': !isOnHoldType ? checkDeliveryTypeAmount(order) : false,
                  }"
                  @click="increase(order)"
                >
                  +
                </span>
                <div class="tooltip-max">
                  <span v-for="type in deliveryTypes" :key="'info' + type.id">
                    <span v-if="type.maxCanisterCount === amountByDeliveryTypeId(order.deliveryTypeId)">
                      Maximal amount ({{ amountByDeliveryTypeId(type.id) }}/{{ type.maxCanisterCount }}) of canisters
                      for
                      {{ $t(type.deliveryTypeTranslationKey).replace(/ .*/, "") }}
                      reached.
                    </span>
                  </span>
                </div>
              </div>
            </td>
            <td v-for="type in deliveryTypes" :key="'checkbox-' + type.id" class="delivery-type">
              <v-checkbox
                :name="order.id.toString()"
                typeInput="radio"
                :value="order.deliveryTypeId === type.id"
                :disabled="deliveryTypeDisabled(order, type)"
                @input="onDeliveryTypeChange(order.id, type.id)"
              />
              <v-small-popup
                v-if="order.rotorConstructionNecessary && deliveryTypeDisabled(order, type)"
                class="order-list-pills__popup"
              >
                <div class="mr-10">
                  <img src="~@/assets/icons/icon-info.svg" />
                </div>
                <div>Rotor construction necessary</div>
              </v-small-popup>
            </td>
          </tr>
          <tr v-if="isFilter && sortedOrders.length > 0">
            <td colspan="10" class="table_filter">{{ $t("page.order.submit.table.filtered") }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <v-order-canister-remove-popup
      v-if="showRemoveMedicinePopup"
      @close="clearRemoveMedicineData()"
      @delete="deleteMedicineFromList()"
      :medicine="medicineNameToRemove"
    />
    <v-free-contingent-used-popup v-if="showUsedPopup" @confirm="confirmUsedPopup()" @close="cancelUsedPopup()" />
  </div>
  <v-loader v-else />
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { VSelect, VCheckbox, VLoader, VSmallPopup } from "shared";
import VOrderCanisterRemovePopup from "@/components/order/v-order-canister-remove-popup";
import VFreeContingentUsedPopup from "@/components/order-submit/v-order-free-contingent-used-popup";
import * as ArrayUtils from "@/utils/ArrayUtils";

export default {
  name: "OrderSubmitPillsTable",
  components: {
    VSelect,
    VCheckbox,
    VLoader,
    VOrderCanisterRemovePopup,
    VFreeContingentUsedPopup,
    VSmallPopup,
  },
  props: {
    orders: {
      type: Array,
      default: null,
    },
    deliveryTypes: {
      type: Array,
      default: null,
    },
    isOnHoldType: {
      type: Boolean,
      default: false,
    },
    freeContingent: {
      type: Boolean,
      default: false,
    },
    isFilter: {
      type: Boolean,
      default: false,
    },
    freeItemsLeft: {
      type: Number,
      default: null,
    },
    freeItemsTotal: {
      type: Number,
      default: null,
    },
    deliveryInfo: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      sortDirection: "asc",
      sortKey: null,
      sortType: "string",
      showRemoveMedicinePopup: false,
      medicineIdToRemove: null,
      medicineNameToRemove: null,
      showUsedPopup: false,
      orderForUsedPopup: null,
    };
  },
  computed: {
    ...mapGetters({
      amountByDeliveryTypeId: "order/onProductionAmountByDeliveryTypeId",
      currentMedKeyTypeName: "medKey/currentMedKeyTypeName",
    }),
    columns() {
      return [
        {
          labelTranslationKey: this.currentMedKeyTypeName,
          key: "medKey",
          type: "string",
        },
        {
          labelTranslationKey: "page.order.submit.table.header.machinename",
          key: "name",
          type: "string",
        },
        {
          labelTranslationKey: "page.order.submit.table.header.medicineid",
          key: "medId",
          type: "string",
        },
        {
          labelTranslationKey: "page.order.submit.table.header.assemblyarticlenumber",
          key: "assemblyArticleNumberSmallUvResistant",
          type: "string",
        },
        // TODO: moved into the assemblyArticleNumberSmallUvResistant - make more generic when next request like this pops up
        // {
        //   labelTranslationKey: "page.order.submit.table.header.rotorarticlenumber",
        //   key: "rotorArticleNumber",
        //   type: "string",
        // },
        {
          labelTranslationKey: "page.order.submit.table.header.canistersize",
          key: "canisterSize",
          type: "string",
        },
        {
          labelTranslationKey: "page.order.submit.table.header.uvresistant",
          key: "uvResistant",
          type: "string",
        },
        {
          labelTranslationKey: "page.order.submit.table.header.amountofcanisters",
          key: "amountOfCanisters",
          type: "number",
        },
      ];
    },
    sortedOrders() {
      const orders = this.orders;
      const stringCompare = (a, b) =>
        this.sortDirection === "asc"
          ? a[this.sortKey].toString().localeCompare(b[this.sortKey].toString())
          : b[this.sortKey].toString().localeCompare(a[this.sortKey].toString());
      const numberCompare = (a, b) =>
        this.sortDirection === "asc" ? a[this.sortKey] - b[this.sortKey] : b[this.sortKey] - a[this.sortKey];
      const compare = this.sortType === "string" ? stringCompare : numberCompare;
      return this.sortKey ? orders.sort(compare) : orders;
    },
  },
  methods: {
    ...mapActions({
      changeDeliveryType: "order/onOrderDeliveryTypeChange",
      changeAmountOfCanisters: "order/onOrderAmountOfCanistersChange",
      removeOrderFromListById: "order/removeOrderFromListById",
    }),
    possibleCanisterSizesOptions(sizes) {
      return sizes.map((s) => {
        return {
          key: s,
          name: s.charAt(0).toUpperCase(),
        };
      });
    },
    onDeliveryTypeChange(orderId, value) {
      this.changeDeliveryType({ orderId, deliveryTypeId: value });
    },
    increase(order) {
      if (!this.isOnHoldType) {
        const amount = ArrayUtils.countElementsByKey(this.orders, "amountOfCanisters");
        const isFreeContingentLeft = this.freeItemsLeft - (amount + 1) >= 0;
        if (this.freeContingent && !isFreeContingentLeft) {
          this.orderForUsedPopup = order;
          this.showUsedPopup = true;
        } else {
          this.orderForUsedPopup = null;
          this.changeAmountOfCanisters({
            orderId: order.id,
            amount: order.amountOfCanisters + 1,
          });
        }
      } else {
        this.changeAmountOfCanisters({
          orderId: order.id,
          amount: order.amountOfCanisters + 1,
        });
      }
    },
    decrease(order) {
      if (order.amountOfCanisters - 1 !== 0) {
        this.changeAmountOfCanisters({
          orderId: order.id,
          amount: order.amountOfCanisters - 1,
        });
      } else {
        this.medicineNameToRemove = order.name;
        this.medicineIdToRemove = order.id;
        this.showRemoveMedicinePopup = true;
      }
    },
    sortByKey($event, { key, type }) {
      this.sortKey = key;
      this.sortType = type;
      if ($event.target.classList.contains("header-column__icon--toggle")) {
        $event.target.classList.remove("header-column__icon--toggle");
        this.sortDirection = "desc";
      } else {
        document.querySelectorAll(".header-column__icon").forEach((el) => {
          if (el.classList.contains("header-column__icon--toggle")) {
            el.classList.remove("header-column__icon--toggle");
          }
        });
        $event.target.classList.add("header-column__icon--toggle");
        this.sortDirection = "asc";
      }
    },
    checkDeliveryTypeAmount(order) {
      const { maxCanisterCount } = this.deliveryTypes.find((dt) => dt.id === order.deliveryTypeId);
      const amount = this.amountByDeliveryTypeId(order.deliveryTypeId);
      return maxCanisterCount
        ? amount === order.amountOfCanisters
          ? order.amountOfCanisters + 1 > maxCanisterCount
          : amount + 1 > maxCanisterCount
        : false;
    },
    deleteMedicineFromList() {
      this.removeOrderFromListById(this.medicineIdToRemove);
      this.clearRemoveMedicineData();
    },
    clearRemoveMedicineData() {
      this.showRemoveMedicinePopup = false;
      this.medicineNameToRemove = null;
      this.medicineIdToRemove = null;
    },
    getAssemblyArticleNumber(order) {
      return order.uvResistant
        ? order.canisterSize === "small"
          ? order.assemblyArticleNumberSmallUvResistant
          : order.assemblyArticleNumberLargeUvResistant
        : order.canisterSize === "small"
        ? order.assemblyArticleNumberSmallNonUvResistant
        : order.assemblyArticleNumberLargeNonUvResistant;
    },
    confirmUsedPopup() {
      this.changeAmountOfCanisters({
        orderId: this.orderForUsedPopup.id,
        amount: this.orderForUsedPopup.amountOfCanisters + 1,
      });
      this.showUsedPopup = false;
      this.orderForUsedPopup = null;
    },
    cancelUsedPopup() {
      this.showUsedPopup = false;
      this.orderForUsedPopup = null;
    },
    deliveryTypeDisabled(order, type) {
      if (!order.availableDeliveryTypes.includes(type.id)) {
        return true;
      }
      if (this.isOnHoldType) {
        return !!type.maxCanisterCount || (this.freeContingent && !type.onHoldConfirmationDays);
      } else {
        if (type.maxCanisterCount) {
          if (this.freeContingent) {
            return true;
          }
          let amount = this.amountByDeliveryTypeId(type.id);
          if (type.id !== order.deliveryTypeId) {
            amount += order.amountOfCanisters;
          }
          return amount > type.maxCanisterCount;
        } else {
          return this.freeContingent && !type.onHoldConfirmationDays;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/components/counter-box";
.tooltip-max {
  position: absolute !important;
  left: 0;
  bottom: 100%;
  background: #fff url("~@/assets/icons/icon-info.svg") no-repeat 16px 18px;
  box-shadow: 0 2px 4px rgba(153, 153, 153, 0.4);
  border-radius: 6px 6px 0 6px;
  width: 210px;
  padding: 18px 10px 10px 57px;
  opacity: 0;
  visibility: hidden;
  z-index: 10;
  transition: all 0.3s;
  color: #999999;
  span {
    margin-bottom: 5px;
    display: block;
  }
}
.tooltip-predefined {
  box-shadow: 0 2px 4px rgba(153, 153, 153, 0.4);
  border-radius: 6px 6px 6px 0;
  position: absolute !important;
  left: 100%;
  bottom: 100%;
  top: auto !important;
  background: #fff url("~@/assets/icons/icon-lock.svg") no-repeat 10px 12px;
  padding: 18px 15px 15px 47px;
  opacity: 0;
  visibility: hidden;
  z-index: 10;
  transition: all 0.3s;
  color: #999999;
  white-space: nowrap;
}
.counter-box {
  &--active-tooltip {
    &:hover {
      .tooltip-max {
        opacity: 1;
        visibility: visible;
        left: 80%;
        bottom: 80%;
      }
    }
  }
  .tooltip-max {
    left: 100%;
  }
}
.relative {
  position: relative;
}
.select {
  &--disabled {
    &:hover {
      & + .tooltip-predefined {
        opacity: 1;
        visibility: visible;
        left: 80%;
        bottom: 80%;
      }
    }
  }
}
.disabled-input:hover {
  & + .tooltip-predefined {
    opacity: 1;
    visibility: visible;
    left: 80%;
    bottom: 80%;
  }
}
.order-list-pills {
  &__table {
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #e4e4e4;
    padding: 16px 30px 10px 32px;
    table {
      width: 100%;
      thead.has-title {
        position: relative;
        th {
          padding-top: 50px;
        }
        .delivery-info {
          position: absolute;
          right: -2px;
          top: -6px;
          color: #ffb032;
          font-size: 12px;
          line-height: 20px;
          width: 166px;
          padding-left: 27px;
          background: url("~@/assets/icons/delivery-info.svg") left top no-repeat;
        }
      }
      th {
        font-size: 13px;
        line-height: 14px;
        letter-spacing: 0.464286px;
        color: #999999;
        border-bottom: 1px solid #ececec;
        padding: 16px 0 16px 0;
        text-align: left;
        position: relative;
        @media all and (max-width: 1200px) {
          font-size: 12px;
          letter-spacing: 0;
        }
        &:hover {
          .tooltip {
            opacity: 1;
            visibility: visible;
          }
        }
        span {
          color: #004593;
          font-weight: bold;
        }
        .header-column__delivery {
          display: flex;
          flex-direction: column;
          &--disabled {
            opacity: 0.4;
            span {
              color: #999999;
            }
          }
        }
        &:nth-child(1) {
          width: 84px;
        }
        &:nth-child(2) {
          width: 231px;
        }
        &:nth-child(3) {
          width: 200px;
          text-align: center;
          border-right: 1px solid #ececec;
          padding: 0 24px 0 24px;
        }
        &:nth-child(4) {
          width: 272px;
          text-align: center;
          border-left: 1px solid #ececec;
          padding: 0 26px 0 35px;
          .header-column__icon {
            right: 20px;
            position: absolute;
          }
        }
        &:nth-child(5) {
          width: 125px;
          text-align: center;
          border-left: 1px solid #ececec;
          padding: 0 30px 0 22px;
          .header-column__icon {
            right: 20px;
            position: absolute;
          }
        }
        &:nth-child(6) {
          width: 62px;
          text-align: center;
          padding-right: 10px;
          .header-column__icon {
            right: 0px;
            position: absolute;
          }
        }
        &:nth-child(7) {
          width: 150px;
          text-align: center;
          border-right: 1px solid #ececec;
          padding: 0 43px 0 32px;
          .header-column__icon {
            right: 30px;
            position: absolute;
          }
        }
        &:nth-child(8) {
          width: 88px;
          text-align: center;
          border-left: 1px solid #ececec;
        }
        &:nth-child(9) {
          width: 60px;
          text-align: center;
        }
        &:nth-child(10) {
          width: 65px;
          text-align: center;
        }
        &:nth-child(11) {
          text-align: center;
        }
        .header-column {
          &__icon {
            width: 14px;
            height: 14px;
            background: url("~@/assets/icons/icon-sort.svg") transparent right no-repeat;
            cursor: pointer;
            display: inline-block;
            vertical-align: middle;
            &--toggle {
              transform: rotate(180deg);
              margin-left: 6px;
              margin-right: -6px;
            }
            &--absolute {
              position: absolute;
              right: 0;
              top: 50%;
              margin-top: -7px;
              z-index: 10;
            }
          }
        }
      }
      td {
        font-size: 13px;
        padding-top: 8px;
        padding-bottom: 11px;
        letter-spacing: 0.5px;
        @media all and (max-width: 1200px) {
          font-size: 12px;
          padding-right: 5px;
        }
        &:nth-child(1) {
          padding-right: 7px;
        }
        &:nth-child(3) {
          text-align: center;
          border-right: 1px solid #ececec;
          padding: 0;
        }
        &:nth-child(4) {
          text-align: center;
          border-left: 1px solid #ececec;
          border-right: 1px solid #ececec;
        }
        &:nth-child(5) {
          padding-left: 10px;
          .select {
            width: 70px;
            margin: 0 auto;
          }
        }
        &:nth-child(6) {
          text-align: center;
          padding-right: 10px;
          div {
            position: relative;
            top: 4px;
          }
        }
        &:nth-child(7) {
          border-right: 1px solid #ececec;
          .counter-box {
            margin: 0 auto;
            width: 103px;
          }
        }
        &:nth-child(8),
        &:nth-child(9),
        &:nth-child(10),
        &:nth-child(11) {
          text-align: center;
          div {
            position: relative;
            top: 4px;
          }
        }
        &.table_filter {
          background: #ececec;
          text-align: center;
        }
      }
    }
  }

  &__popup {
    top: -52px !important;
    right: 52px;
    display: none;
    position: absolute !important;

    td:hover & {
      display: flex;
    }
  }
}

.delivery-type {
  position: relative;
}

.tooltip {
  position: absolute;
  left: 0;
  bottom: 100%;
  background: #fff url("~@/assets/icons/icon-info.svg") no-repeat 16px 18px;
  box-shadow: 0px 2px 4px rgba(153, 153, 153, 0.4);
  border-radius: 6px 6px 0px 6px;
  width: 162px;
  padding: 18px 10px 15px 57px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
}
::v-deep .options {
  width: 100%;
}
</style>
